import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import slugify from 'slugify';
import { api } from '~/lib/api-client';
import { useSession } from '~/lib/auth';
import { buildHead } from '~/seo/site';

export const Route = createFileRoute('/genres/propose')({
  head: () =>
    buildHead({
      title: 'Propose a Genre',
      description:
        'Suggest a new genre for beat battles. Define stem types, set the BPM range, and let the community vote it in.',
      path: '/genres/propose',
    }),
  component: ProposeGenre,
});

// Must mirror STEM_TYPES on the API side (src/routes/genres.ts).
const STEM_TYPES = [
  'kick',
  'snare',
  'hihat',
  'openhat',
  'clap',
  'perc',
  'fx',
  '808',
  'bass',
  'lead',
  'pad',
  'vocal',
  'zap',
  'screech',
  'reverse',
  'cowbell',
];

// Live preview of what the API will compute. Uses the same library (slugify)
// with the same options as src/routes/genres.ts on the API.
function previewSlug(name: string): string {
  return slugify(name, { lower: true, strict: true, trim: true, locale: 'en' }).slice(0, 32);
}

// Reusable form body. Calls onDone() on success - the caller decides whether to
// navigate (the standalone /genres/propose route) or refresh in place (Studio).
export function ProposeGenreForm({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState('');
  const [stems, setStems] = useState<string[]>(['kick', 'snare', 'hihat', 'fx']);
  const [err, setErr] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  function toggleStem(s: string) {
    setStems((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s].slice(0, 12),
    );
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (stems.length < 3) {
      setErr('Pick at least 3 stem types.');
      return;
    }
    setPending(true);
    try {
      await api('/genres', { method: 'POST', body: JSON.stringify({ name, stemTypes: stems }) });
      setName('');
      setStems(['kick', 'snare', 'hihat', 'fx']);
      onDone();
    } catch (e2) {
      setErr((e2 as Error).message);
    } finally {
      setPending(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <label className="block text-sm">
        <span className="mb-1 block text-muted-foreground">Name</span>
        <input
          type="text"
          required
          minLength={3}
          maxLength={64}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Future funk"
          className="w-full rounded-md border border-border bg-background px-3 py-2"
        />
        {name && (
          <span className="mt-1 block text-xs text-muted-foreground">
            URL slug:{' '}
            <code className="font-mono text-foreground">
              {previewSlug(name) || '(needs at least 3 letters)'}
            </code>
          </span>
        )}
      </label>

      <div className="text-sm">
        <div className="mb-2 flex items-baseline justify-between">
          <span className="text-muted-foreground">Stem types (3-12)</span>
          <span className="text-xs tabular-nums">{stems.length}/12</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {STEM_TYPES.map((s) => {
            const on = stems.includes(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggleStem(s)}
                className={`rounded-full border px-3 py-1 text-xs ${
                  on
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border text-muted-foreground hover:text-foreground'
                }`}
              >
                {s}
              </button>
            );
          })}
        </div>
      </div>

      {err && <p className="text-sm text-red-500">{err}</p>}
      <button
        type="submit"
        disabled={pending}
        className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
      >
        {pending ? 'Proposing...' : 'Propose'}
      </button>
    </form>
  );
}

function ProposeGenre() {
  const { data: session, isLoading } = useSession();
  const navigate = useNavigate();

  if (isLoading) return <section className="p-8">Loading...</section>;
  if (!session) {
    return (
      <section className="mx-auto max-w-md px-4 py-16 text-center space-y-3">
        <h2 className="text-2xl font-bold">Sign in to propose a genre</h2>
        <a href="/auth/sign-in" className="underline">
          Sign in
        </a>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-md px-4 py-12 space-y-5">
      <header>
        <h2 className="text-2xl font-bold">Propose a genre</h2>
        <p className="text-muted-foreground text-sm">
          Pick the stem types matches in this genre will need. Community votes for 7 days - hit 3
          votes and the genre goes public.
        </p>
      </header>
      <ProposeGenreForm onDone={() => navigate({ to: '/genres' })} />
    </section>
  );
}
